import { useState, useCallback, useContext } from "react";
import { useMutation } from "@tanstack/react-query";
import { queryClient } from "@/queryClient";
import {
  TOAST_IDS,
  ToastType,
  DeveloperTrackItem,
} from "@/modules/common/types";
import { showToast } from "@/modules/common/utils";
import { LeadPageContext } from "@/modules/lead-page/context/lead-page.context";
import { createLeadTracksBulk } from "../api";
import { LEAD_TRACKS_QUERY_KEY } from "./use-lead-tracks.hook";

interface UseCreateTrackProps {
  onSuccess?: () => void;
  onClose?: () => void;
}

export interface TrackErrors {
  [trackId: string]: {
    developer?: boolean;
  };
}

export const useCreateTrack = ({ onSuccess, onClose }: UseCreateTrackProps) => {
  const { lead } = useContext(LeadPageContext);
  const leadAny = lead?.data?.lead as any;
  const leadUId = leadAny?.uid;

  const [createdTracks, setCreatedTracks] = useState<DeveloperTrackItem[]>([
    {
      id: crypto.randomUUID(),
      leadUId: leadUId || "",
      developerUId: "",
      developerUserUId: "",
    },
  ]);

  const [trackErrors, setTrackErrors] = useState<TrackErrors>({});

  const handleAddTrack = useCallback(() => {
    setCreatedTracks((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        leadUId: leadUId || "",
        developerUId: "",
        developerUserUId: "",
      },
    ]);
  }, [leadUId]);

  const handleDeleteTrack = useCallback((id: string) => {
    setCreatedTracks((prev) => prev.filter((track) => track.id !== id));
    // Also clear error for deleted track
    setTrackErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[id];
      return newErrors;
    });
  }, []);

  const handleTrackChange = useCallback(
    (id: string, developerUId: string, developerUserUId: string) => {
      setCreatedTracks((prev) =>
        prev.map((track) =>
          track.id === id ? { ...track, developerUId, developerUserUId } : track
        )
      );
      // Clear errors for this track when values change
      setTrackErrors((prev) => {
        const newErrors = { ...prev };
        if (newErrors[id]) {
          if (developerUId) delete newErrors[id]?.developer;
          if (!newErrors[id]?.developer) {
            delete newErrors[id];
          }
        }
        return newErrors;
      });
    },
    []
  );

  // Validate tracks and return true if valid
  const validateTracks = useCallback((): boolean => {
    const errors: TrackErrors = {};
    let hasErrors = false;

    createdTracks.forEach((track) => {
      if (!track.developerUId) {
        errors[track.id] = { developer: true };
        hasErrors = true;
      }
    });

    setTrackErrors(errors);
    return !hasErrors;
  }, [createdTracks]);

  // Mutation to create tracks via bulk endpoint
  const { mutate: createTrack, isLoading: isSubmitting } = useMutation({
    mutationFn: async (
      bulkData: {
        leadUId: string;
        developerUId: string;
        developerUserUId?: string;
      }[]
    ) => {
      return createLeadTracksBulk(bulkData);
    },
    onSuccess: () => {
      showToast({
        type: ToastType.SUCCESS,
        message: "Tracks created successfully",
        toastId: TOAST_IDS.ADD_TRACK_SUCCESS,
        options: { position: "bottom-left", autoClose: 3000 },
      });

      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ["getLead"] });
      queryClient.invalidateQueries({ queryKey: [LEAD_TRACKS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ["lead-activity"] });

      onSuccess?.();
      onClose?.();

      // Reset state
      setCreatedTracks([
        {
          id: crypto.randomUUID(),
          leadUId: leadUId || "",
          developerUId: "",
          developerUserUId: "",
        },
      ]);
      setTrackErrors({});
    },
    onError: (error: any) => {
      showToast({
        type: ToastType.ERROR,
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to create tracks",
        toastId: TOAST_IDS.ADD_TRACK_ERROR,
        options: { position: "bottom-left", autoClose: 3000 },
      });
    },
  });

  const handleSubmit = useCallback(() => {
    if (!leadUId) {
      console.error("Missing required data: leadUId");
      return;
    }

    // Validate before submitting
    if (!validateTracks()) {
      return;
    }

    // Get valid tracks
    const validTracks = createdTracks.filter((t) => t.developerUId);

    if (validTracks.length === 0) {
      return;
    }

    // Check for duplicate developers
    const developerIds = validTracks.map((t) => t.developerUId);
    const uniqueDeveloperIds = new Set(developerIds);
    if (developerIds.length !== uniqueDeveloperIds.size) {
      showToast({
        type: ToastType.ERROR,
        message: "You cannot create multiple tracks for the same developer",
        toastId: TOAST_IDS.ADD_TRACK_ERROR,
        options: { position: "bottom-left", autoClose: 3000 },
      });
      return;
    }

    const bulkData = validTracks.map((t) => ({
      leadUId: leadUId,
      developerUId: t.developerUId,
      developerUserUId: t.developerUserUId || undefined,
    }));

    createTrack(bulkData);
  }, [createTrack, validateTracks, createdTracks, leadUId]);

  // Check if at least one track is valid (has developer)
  const isValid = createdTracks.some((t) => t.developerUId);

  // Check if we can add a new track (all existing tracks have a developer selected)
  const canAddNewTrack = createdTracks.every((t) => t.developerUId);

  // Reset function to clear all tracks
  const resetTracks = useCallback(() => {
    setCreatedTracks([
      {
        id: crypto.randomUUID(),
        leadUId: leadUId || "",
        developerUId: "",
        developerUserUId: "",
      },
    ]);
    setTrackErrors({});
  }, [leadUId]);

  return {
    createdTracks,
    handleAddTrack,
    handleDeleteTrack,
    handleTrackChange,
    resetTracks,
    isSubmitting,
    handleSubmit,
    isValid,
    canAddNewTrack,
    trackErrors,
  };
};

export default useCreateTrack;
